import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { fetchData } from '../services/fetchData';
import { getProjectsAllUrl } from '../utils/projects';

const AppContext = createContext();

const currentLeadTime = '9-10 weeks';
const THEME_STORAGE_KEY = 'theme';

const readStoredTheme = () => {
    try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        if (saved === 'dark' || saved === 'light') return saved;
    } catch (_) {
        /* ignore */
    }
    return 'light';
};

export const AppProvider = ({ children }) => {
    const base_url = 'https://mts-core-a3cdcd5c1cb2.herokuapp.com';
    // const base_url = 'https://mts-core-prod-82e4d823398b.herokuapp.com';
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [company, setCompany] = useState(null);
    const [isLoadingCompany, setIsLoadingCompany] = useState(true);
    const [projects, setProjects] = useState([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
    const [quotes, setQuotes] = useState([]);
    const [isLoadingQuotes, setIsLoadingQuotes] = useState(true);
    const [includeArchived, setIncludeArchived] = useState(false);
    const [theme, setThemeState] = useState(() => {
        const attr = document.documentElement.getAttribute('data-theme');
        if (attr === 'dark' || attr === 'light') return attr;
        return readStoredTheme();
    });

    // Check for existing token in localStorage on app startup
    useEffect(() => {
        const savedToken = localStorage.getItem('authToken');
        if (savedToken) {
            setToken(savedToken);
        }
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        try {
            localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch (_) {
            /* ignore */
        }
    }, [theme]);

    const setTheme = useCallback((next) => {
        if (next === 'dark' || next === 'light') {
            setThemeState(next);
        }
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeState((current) => (current === 'dark' ? 'light' : 'dark'));
    }, []);
    // Custom setToken function that handles localStorage
    const setTokenWithStorage = (newToken, rememberMe = false) => {
        setToken(newToken);
        if (rememberMe && newToken) {
            localStorage.setItem('authToken', newToken);
        } else if (!rememberMe) {
            localStorage.removeItem('authToken');
        }
    };

    const refreshAppState = useCallback(async () => {
        if (!token) return;

        setIsLoadingCompany(true);
        setIsLoadingProjects(true);
        setIsLoadingInvoices(true);
        setIsLoadingQuotes(true);

        try {
            const [profile, projectsResult, invoicesResult] = await Promise.all([
                fetchData(token, `${base_url}/apis/admin/profile`),
                fetchData(token, getProjectsAllUrl(base_url)),
                fetchData(token, `${base_url}/apis/billing/invoices`),
            ]);

            if (profile?.user) {
                setUser(profile.user);
            }

            if (profile?.company) {
                setCompany(profile.company);
            }

            if (projectsResult?.projects) {
                setProjects(projectsResult.projects);
            }

            if (invoicesResult?.invoices) {
                setInvoices(invoicesResult.invoices);
            }

            const isBillingUser = Boolean(profile?.user?.is_admin || profile?.user?.is_super_admin);
            if (isBillingUser) {
                const quotesResult = await fetchData(token, `${base_url}/apis/billing`);
                if (quotesResult?.quotes) {
                    setQuotes(quotesResult.quotes);
                }
            } else {
                setQuotes([]);
            }
        } catch (error) {
            console.error('Error refreshing app state:', error);
        } finally {
            setIsLoadingCompany(false);
            setIsLoadingProjects(false);
            setIsLoadingInvoices(false);
            setIsLoadingQuotes(false);
        }
    }, [token, base_url, setUser, setCompany, setProjects, setInvoices, setQuotes]);

    // Function to clear token and logout
    const logout = () => {
        setToken(null);
        setUser(null);
        setCompany(null);
        setProjects([]);
        setInvoices([]);
        setQuotes([]);
        setIncludeArchived(false);
        localStorage.removeItem('authToken');
    };

    return (
        <AppContext.Provider value={{ 
            isLoadingProjects, setIsLoadingProjects,
            isLoadingCompany, setIsLoadingCompany,
            isLoadingInvoices, setIsLoadingInvoices,
            isLoadingQuotes, setIsLoadingQuotes,
            projects, setProjects,
            company, setCompany,
            invoices, setInvoices,
            quotes, setQuotes,
            token, setToken: setTokenWithStorage,
            user, setUser,
            base_url,
            logout,
            refreshAppState,
            includeArchived, setIncludeArchived,
            currentLeadTime,
            theme, setTheme, toggleTheme,
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
}; 